/**
 * ScenarioEditorDialogs
 *
 * Contains all dialogs and snackbars for the ScenarioEditor:
 * - Rename scenario dialog
 * - Delete scenario dialog
 * - Remote update toast
 * - General snackbar for notifications
 */

import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    Snackbar,
    Alert,
} from '@mui/material';
import { vortxColors } from '@vortx/shared-ui';

/**
 * @param {Object} props
 * @param {boolean} props.renameDialogOpen - Whether rename dialog is open
 * @param {Function} props.onCloseRenameDialog - Callback to close rename dialog
 * @param {string} props.tempScenarioName - Temporary name being edited
 * @param {Function} props.onTempNameChange - Callback when temp name changes
 * @param {Function} props.onSaveRename - Callback to save the new name
 * @param {boolean} props.deleteDialogOpen - Whether delete dialog is open
 * @param {Function} props.onCloseDeleteDialog - Callback to close delete dialog
 * @param {Function} props.onDelete - Callback to delete scenario
 * @param {boolean} props.remoteUpdateToast - Whether remote update toast is visible
 * @param {Function} props.onCloseRemoteToast - Callback to close remote update toast
 * @param {Object} props.snackbar - Snackbar state { open, message, severity }
 * @param {Function} props.onCloseSnackbar - Callback to close snackbar
 */
export default function ScenarioEditorDialogs({
    renameDialogOpen,
    onCloseRenameDialog,
    tempScenarioName,
    onTempNameChange,
    onSaveRename,
    deleteDialogOpen,
    onCloseDeleteDialog,
    onDelete,
    remoteUpdateToast,
    onCloseRemoteToast,
    snackbar,
    onCloseSnackbar,
}) {
    return (
        <>
            {/* Rename Dialog */}
            <Dialog open={renameDialogOpen} onClose={onCloseRenameDialog}>
                <DialogTitle>Rename Scenario</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Scenario Name"
                        value={tempScenarioName}
                        onChange={(e) => onTempNameChange(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCloseRenameDialog}>Cancel</Button>
                    <Button
                        onClick={onSaveRename}
                        sx={{ color: vortxColors.gold }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={onCloseDeleteDialog}>
                <DialogTitle>Delete Scenario</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this scenario? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={onDelete} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Remote Update Toast */}
            <Snackbar
                open={remoteUpdateToast}
                autoHideDuration={3000}
                onClose={onCloseRemoteToast}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={onCloseRemoteToast} severity="info">
                    Scenario updated in another tab
                </Alert>
            </Snackbar>

            {/* General Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={onCloseSnackbar}
            >
                <Alert onClose={onCloseSnackbar} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
